import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { requestOTP, verifyOTP } from '../services/api';
import { HeaderBar } from '../components/ui';
import { useTranslation } from '../i18n/useTranslation';
import { motion } from 'framer-motion';
import './Login.css';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthData, showToast, authData } = useApp();
  const { t } = useTranslation();
  
  const [step, setStep] = useState('phone'); // 'phone', 'otp', 'register'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [gender, setGender] = useState('');
  const [hidePhone, setHidePhone] = useState(false); // Privacy: hide phone from others
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isNewUser, setIsNewUser] = useState(false);

  const otpInputRefs = useRef([]);
  const abortControllerRef = useRef(null);
  
  // Normalize return path: never send to /dems, use main app paths instead
  const normalizeReturnTo = (path) => {
    if (!path || typeof path !== 'string') return '/';
    if (path.startsWith('/dems/event/')) return path.replace(/^\/dems/, '');
    if (path.startsWith('/dems')) return '/';
    return path;
  };

  // Redirect if already logged in
  useEffect(() => {
    if (authData) {
      const returnTo = normalizeReturnTo(location.state?.returnTo || '/');
      navigate(returnTo, { replace: true });
    }
  }, [authData, navigate, location]);
  
  // Countdown timer for resend OTP
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);
  
  const handleOtpAutoSubmit = useCallback(async (otpCode) => {
    if (otpCode.length === 6) {
      try {
        setLoading(true);
        const response = await verifyOTP(phone, otpCode, isNewUser ? name : null, isNewUser ? email : null, isNewUser ? gender : null, isNewUser ? hidePhone : false);
        
        if (response.requires_registration) {
          setIsNewUser(true);
          setStep('register');
          showToast(t('login.pleaseCompleteRegistration'), 'info');
          return;
        }
        
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('authAccount', JSON.stringify(response.account));
        
        setAuthData(response.account);
        showToast(`${t('login.welcome')}${response.account.name ? ', ' + response.account.name : ''}!`, 'success');
        
        const returnTo = normalizeReturnTo(location.state?.returnTo || '/');
        navigate(returnTo, { replace: true });
      } catch (error) {
        showToast(error.message || t('login.invalidOTP'), 'error');
        setOtp(['', '', '', '', '', '']);
        otpInputRefs.current[0]?.focus();
      } finally {
        setLoading(false);
      }
    }
  }, [phone, isNewUser, name, email, gender, hidePhone, showToast, setAuthData, navigate, location, t]);

  // Auto-read OTP from SMS
  useEffect(() => {
    if (step === 'otp' && 'OTPCredential' in window) {
      abortControllerRef.current = new AbortController();
      
      navigator.credentials.get({
        otp: { transport: ['sms'] },
        signal: abortControllerRef.current.signal
      }).then(otpCredential => {
        if (otpCredential && otpCredential.code) {
          const otpCode = otpCredential.code;
          const otpArray = otpCode.split('').slice(0, 6);
          setOtp(otpArray);
          setTimeout(() => handleOtpAutoSubmit(otpArray.join('')), 100);
        }
      }).catch(() => {});
      
      return () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      };
    }
  }, [step, handleOtpAutoSubmit]);
  
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    
    // Sanitize phone number - remove all non-digits, then take only the last 10 digits
    // This prevents browser autocomplete from concatenating multiple numbers
    const cleanedPhone = phone.replace(/\D/g, '');
    
    // Israeli phone numbers should be 9-10 digits (with or without leading 0)
    // If longer, take the last 10 digits (browser might have concatenated)
    let sanitizedPhone = cleanedPhone;
    if (cleanedPhone.length > 10) {
      console.warn(`Phone number too long (${cleanedPhone.length} digits), taking last 10 digits`);
      sanitizedPhone = cleanedPhone.slice(-10);
    }
    
    // Add leading 0 if missing (Israeli format)
    if (sanitizedPhone.length === 9 && !sanitizedPhone.startsWith('0')) {
      sanitizedPhone = '0' + sanitizedPhone;
    }
    
    // Validate final phone number
    if (!sanitizedPhone || sanitizedPhone.length !== 10 || !sanitizedPhone.startsWith('0')) {
      showToast(t('login.invalidPhone'), 'error');
      return;
    }
    
    // Update phone state with sanitized value
    setPhone(sanitizedPhone);
    
    setLoading(true);
    
    try {
      const response = await requestOTP(sanitizedPhone);
      setIsNewUser(response.is_new_user ?? !response.account_exists);

      if (response.requires_registration) {
        setStep('register');
        showToast(t('login.fillDetails'), 'info');
        return;
      }

      // OTP skipped (e.g. DISABLE_OTP_SMS): backend returned token + account — log in immediately
      if (response.success && response.token && response.account) {
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('authAccount', JSON.stringify(response.account));
        setAuthData(response.account);
        showToast(`${t('login.welcome')}${response.account.name ? ', ' + response.account.name : ''}!`, 'success');
        const returnTo = normalizeReturnTo(location.state?.returnTo || '/');
        navigate(returnTo, { replace: true });
        return;
      }

      if (response.success && response.requires_otp) {
        setStep('otp');
        setCountdown(60);
        showToast(t('login.otpSent') || 'Verification code sent.', 'success');
        return;
      }

      setStep('register');
      showToast(t('login.fillDetails'), 'info');
    } catch (error) {
      const errorMessage = error.message || 'Failed to login';
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
    
    if (newOtp.every(digit => digit) && newOtp.join('').length === 6) {
      handleOtpAutoSubmit(newOtp.join(''));
    }
  };
  
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };
  
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const otpArray = pastedData.split('');
      setOtp(otpArray);
      handleOtpAutoSubmit(pastedData);
    }
  };
  
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      showToast(t('login.enter6DigitOTP'), 'error');
      return;
    }
    await handleOtpAutoSubmit(otpCode);
  };
  
  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      showToast(t('login.pleaseEnterName'), 'error');
      return;
    }
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      showToast(t('login.pleaseEnterValidEmail'), 'error');
      return;
    }
    if (!gender) {
      showToast(t('login.pleaseSelectGender'), 'error');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await requestOTP(phone, name, email, gender, hidePhone);

      if (response.requires_registration) {
        showToast(t('login.fillDetails'), 'info');
        return;
      }

      // OTP skipped: backend returned token + account — log in immediately
      if (response.success && response.token && response.account) {
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('authAccount', JSON.stringify(response.account));
        setAuthData(response.account);
        showToast(`${t('login.welcome')}${response.account.name ? ', ' + response.account.name : ''}!`, 'success');
        const returnTo = normalizeReturnTo(location.state?.returnTo || '/');
        navigate(returnTo, { replace: true });
        return;
      }

      if (response.success && response.requires_otp) {
        setStep('otp');
        setCountdown(60);
        showToast(t('login.otpSent') || 'Verification code sent.', 'success');
        return;
      }

      showToast(response.message || t('login.registrationFailed') || 'Please try again.', 'error');
    } catch (error) {
      const errorMessage = error.message || 'Registration failed';
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleResendOTP = async () => {
    if (countdown > 0) return;
    
    setLoading(true);
    try {
      await requestOTP(phone);
      setCountdown(60);
      showToast(t('login.otpResent'), 'success');
    } catch (error) {
      showToast(error.message || t('login.failedToResendOTP'), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <HeaderBar />
      
      <div className="login-main">
        <motion.div 
          className="login-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Header */}
          <div className="login-header">
            <img src="/images/trempi-logo.png" alt="Trempi" className="login-logo" />
            <h1>
              {step === 'phone' && t('login.title')}
              {step === 'register' && t('login.createAccount')}
              {step === 'otp' && t('login.verify')}
            </h1>
            <p>
              {step === 'phone' && t('login.subtitle')}
              {step === 'register' && t('login.fillDetails')}
              {step === 'otp' && t('login.enterOTP')}
            </p>
          </div>
          
          {/* Phone Step */}
          {step === 'phone' && (
            <form onSubmit={handleRequestOTP} className="login-form">
              <div className="form-group">
                <label>{t('login.phoneNumber')}</label>
                <input
                  type="tel"
                  className="form-input"
                  value={phone}
                  onChange={(e) => {
                    // Only allow digits, limit to 10 digits
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setPhone(value);
                  }}
                  placeholder={t('login.phonePlaceholder')}
                  autoComplete="tel-national"
                  maxLength={10}
                  autoFocus
                />
                <span className="form-hint">{t('login.smsHint')}</span>
              </div>
              
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? t('login.sending') : t('common.next')}
              </button>
            </form>
          )}
          
          {/* Register Step */}
          {step === 'register' && (
            <form onSubmit={handleRegister} className="login-form">
              <div className="step-badge">New Account</div>
              
              <div className="form-group">
                <label>{t('login.name')} *</label>
                <input
                  type="text"
                  className="form-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('login.namePlaceholder')}
                  autoFocus
                />
              </div>
              
              <div className="form-group">
                <label>{t('login.email')} *</label>
                <input
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('login.emailPlaceholder')}
                  autoComplete="email"
                />
              </div>
              
              <div className="form-group">
                <label>{t('login.phoneNumber')}</label>
                <input
                  type="tel"
                  className="form-input"
                  value={phone}
                  disabled
                />
              </div>
              
              <div className="form-group">
                <label>{t('login.gender')} *</label>
                <div className="gender-options">
                  <label className={`gender-option ${gender === 'male' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="gender"
                      value="male"
                      checked={gender === 'male'}
                      onChange={(e) => setGender(e.target.value)}
                    />
                    <span>👨 {t('login.genderMale')}</span>
                  </label>
                  <label className={`gender-option ${gender === 'female' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="gender"
                      value="female"
                      checked={gender === 'female'}
                      onChange={(e) => setGender(e.target.value)}
                    />
                    <span>👩 {t('login.genderFemale')}</span>
                  </label>
                </div>
              </div>
              
              {/* Privacy Option */}
              <div className="form-group privacy-option">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={hidePhone}
                    onChange={(e) => setHidePhone(e.target.checked)}
                  />
                  <span className="checkbox-text">
                    🔒 {t('login.hidePhone') || 'Hide my phone number'}
                  </span>
                </label>
                <p className="privacy-hint">
                  {t('login.hidePhoneHint') || 'Others can only contact you after you approve their request'}
                </p>
              </div>
              
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? t('login.sendingOTP') : t('login.register')}
              </button>
              
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setStep('phone');
                  setName('');
                  setEmail('');
                }}
              >
                ← {t('common.back')}
              </button>
            </form>
          )}
          
          {/* OTP Step */}
          {step === 'otp' && (
            <form onSubmit={handleVerifyOTP} className="login-form">
              <div className="otp-info">
                <p>{t('login.codeSentTo')} <strong>{phone}</strong></p>
                <button
                  type="button"
                  className="change-btn"
                  onClick={() => {
                    setStep(isNewUser ? 'register' : 'phone');
                    setOtp(['', '', '', '', '', '']);
                  }}
                >
                  {t('common.edit')}
                </button>
              </div>
              
              <div className="otp-inputs" onPaste={handleOtpPaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (otpInputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    className="otp-input"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    autoFocus={index === 0}
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                  />
                ))}
              </div>
              
              <button type="submit" className="btn-primary" disabled={loading || otp.some(d => !d)}>
                {loading ? t('login.verifying') : t('login.verify') + ' & ' + t('header.loginSignUp')}
              </button>
              
              <div className="resend-section">
                {countdown > 0 ? (
                  <p>{t('login.resendIn')} {countdown}{t('login.seconds')}</p>
                ) : (
                  <button type="button" className="resend-btn" onClick={handleResendOTP} disabled={loading}>
                    {t('login.resendOTP')}
                  </button>
                )}
              </div>
            </form>
          )}
          
          <div className="login-footer">
            <p>{t('login.termsOfService')}</p>
          </div>
        </motion.div>
      </div>
      
      {/* Loading Overlay */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>{step === 'otp' ? t('login.verifying') : t('login.sending')}</p>
        </div>
      )}
    </div>
  );
}

export default Login;
